import { useEffect, useState, useMemo } from 'react';
import {
  FieldExtensionComponentProps,
  CustomFieldExtensionSchema,
  createScaffolderFieldExtension,
} from '@backstage/plugin-scaffolder-react';
import { scaffolderPlugin } from '@backstage/plugin-scaffolder';
import {
  FormControl,
  TextField,
  FormHelperText,
  Link,
} from '@material-ui/core';
import Autocomplete from '@mui/material/Autocomplete';
import { renderLoadingAdornment } from './fieldUtils';
import { useNobl9Metadata } from '@internal/plugin-nobl9-metadata-frontend';

export const Nobl9ProjectFieldSchema: CustomFieldExtensionSchema = {
  returnValue: { type: 'string' },
};

interface Props extends FieldExtensionComponentProps<string> {}

export const Nobl9ProjectFieldComponent = ({
  onChange,
  formData,
  rawErrors,
  schema,
}: Props) => {
  const { projects, state, error, fallbackReason, sourceUsed } =
    useNobl9Metadata();
  const [value, setValue] = useState(formData || '');
  // Propagate slug value only when valid
  useEffect(() => {
    if (value) {
      const slugOk = /^[a-z0-9]+[a-z0-9-]*[a-z0-9]+$/.test(value);
      if (slugOk) onChange(value);
    }
  }, [value, onChange]);

  const loading = state === 'loading' || state === 'refreshing';

  useEffect(() => {
    // Removed ready-state diagnostics
  }, [loading, sourceUsed, fallbackReason, projects]);

  // Build sorted & grouped project option objects
  const sortedProjects = useMemo(() => {
    const mapped = projects.map(p => ({
      name: p.name,
      displayName: p.displayName || p.name,
      bu: (p as any).bu || 'Other',
      appInventoryId: (p as any).appInventoryId,
      pfgOwner: (p as any).pfgOwner ?? '',
      label: (p as any).appInventoryId
        ? `${p.displayName || p.name} (${(p as any).appInventoryId})`
        : p.displayName || p.name,
    }));
    return mapped.sort((a, b) => {
      const buCmp = a.bu.localeCompare(b.bu);
      if (buCmp !== 0) return buCmp;
      return a.displayName.localeCompare(b.displayName);
    });
  }, [projects]);

  const rawDescription: string = (schema as any)?.description || '';
  // Convert markdown links [text](url) into clickable MUI Link components while preserving line breaks.
  const descriptionNodes = useMemo(() => {
    if (!rawDescription) return null;
    const parts: (string | JSX.Element)[] = [];
    const regex = /\[([^\]]+)\]\((https?:\/\/[^\s)]+)\)/g;
    let lastIndex = 0;
    for (const m of rawDescription.matchAll(regex)) {
      if (m.index! > lastIndex) {
        parts.push(rawDescription.slice(lastIndex, m.index!));
      }
      parts.push(
        <Link
          href={m[2]}
          target="_blank"
          rel="noopener noreferrer"
          underline="always"
          color="inherit"
        >
          {m[1]}
        </Link>,
      );
      lastIndex = (m.index || 0) + m[0].length;
    }
    if (lastIndex < rawDescription.length) {
      parts.push(rawDescription.slice(lastIndex));
    }
    return parts;
  }, [rawDescription]);
  const statusLine = useMemo(() => {
    if (loading) return 'Loading projects…';
    if (error) return 'Error loading projects';
    if (!projects.length) return 'No projects available';
    return '';
  }, [loading, error, projects.length]);
  return (
    <FormControl
      fullWidth
      margin="dense"
      error={!!rawErrors?.length || !!error}
    >
      <Autocomplete
        disabled={loading}
        options={sortedProjects}
        groupBy={opt => opt.bu}
        getOptionLabel={opt => opt.label}
        value={
          value ? sortedProjects.find(p => p.name === value) || null : null
        }
        onChange={(_e, newOpt) => {
          if (!newOpt) {
            setValue('');
            (window as any).__nobl9SelectedProjectName = undefined;
            (window as any).__nobl9SelectedProjectId = undefined;
            (window as any).__nobl9SelectedProjectInternalId = undefined;
            (window as any).__nobl9SelectedProjectPfgOwner = undefined;
            return;
          }
          setValue(newOpt.name);
          (window as any).__nobl9SelectedProjectName = newOpt.name;

          // For linkage to services we MUST use the project slug (name) because
          // service.projectId currently derives from the upstream "project" field (slug).
          (window as any).__nobl9SelectedProjectId = newOpt.name; // linkage key consumed by Service select
          (window as any).__nobl9SelectedProjectInternalId =
            newOpt.appInventoryId; // informational only (may be undefined)
          (window as any).__nobl9SelectedProjectPfgOwner = newOpt.pfgOwner; // consumed by PfgOwner field

          // Selection diagnostics removed
        }}
        loading={loading}
        renderInput={params => (
          <TextField
            {...params}
            label="Nobl9 Project"
            placeholder="Start typing to search…"
            InputProps={{
              ...params.InputProps,
              endAdornment: renderLoadingAdornment(params, loading),
            }}
          />
        )}
        filterSelectedOptions
        autoHighlight
        clearOnBlur={false}
        openOnFocus
        noOptionsText={loading ? 'Loading…' : 'No matching projects'}
        isOptionEqualToValue={(opt, val) => opt.name === (val?.name || val)}
      />
      {(descriptionNodes || statusLine) && (
        <FormHelperText component="div" style={{ whiteSpace: 'pre-line' }}>
          {descriptionNodes}
          {descriptionNodes && statusLine ? '\n' : ''}
          {statusLine}
        </FormHelperText>
      )}
    </FormControl>
  );
};

export const Nobl9ProjectFieldExtension = scaffolderPlugin.provide(
  createScaffolderFieldExtension({
    name: 'Nobl9_ProjectSelect',
    component: Nobl9ProjectFieldComponent,
    schema: Nobl9ProjectFieldSchema,
  }),
);

export default Nobl9ProjectFieldExtension;
